import React from "react";
import { Link } from "react-router-dom";
import { Wine } from "lucide-react";

// Effective date must match auth/handler.py's CONSENT_VERSION constant —
// bump both together whenever this policy materially changes.
const EFFECTIVE_DATE = "23 July 2026";
// TODO before this goes live with real users: replace with the actual
// controller entity name and a monitored contact address.
const CONTACT_EMAIL = "tourgourmania@gmail.com";

const S = {
  page: { minHeight: "100vh", background: "#F2F5EC", fontFamily: "'Inter',sans-serif", padding: "32px 20px 80px" },
  box: { maxWidth: 720, margin: "0 auto" },
  h1: { fontFamily: "'Playfair Display',serif", fontSize: 26, color: "#1E2C14", margin: "0 0 4px" },
  meta: { fontSize: 12, color: "#6A7E58", marginBottom: 28 },
  h2: { fontSize: 15, fontWeight: 700, color: "#2C3820", margin: "28px 0 8px" },
  p: { fontSize: 13, color: "#3A4A2E", lineHeight: 1.6, margin: "0 0 12px" },
  li: { fontSize: 13, color: "#3A4A2E", lineHeight: 1.6, margin: "0 0 6px" },
  link: { color: "#886070", textDecoration: "none" },
};

export default function PrivacyPage() {
  return (
    <div style={S.page}>
      <div style={S.box}>
        <Wine size={24} color="#6B2737" style={{ marginBottom: 10 }} />
        <h1 style={S.h1}>Privacy Policy</h1>
        <p style={S.meta}>Effective {EFFECTIVE_DATE} · <Link to="/terms" style={S.link}>Terms of Service</Link></p>

        <p style={S.p}>
          This policy explains what personal data Wine4Collect ("we", "us") collects when you plan a wine tour,
          book a tasting, or manage a winery listing, why we collect it, how long we keep it, and the rights
          you have over it under the EU General Data Protection Regulation (GDPR).
        </p>

        <h2 style={S.h2}>What we collect</h2>
        <ul style={{ margin: "0 0 12px", paddingLeft: 18 }}>
          <li style={S.li}><strong>Traveler accounts:</strong> name, email, password (stored as a one-way hash, never in plain text), favorite wines, and plan preferences you save.</li>
          <li style={S.li}><strong>Winery manager / road rep / admin accounts:</strong> name, email, phone, organization, password hash.</li>
          <li style={S.li}><strong>Bookings:</strong> your name, email, party size, and requested date/time, shared with the winery you book so they can confirm your visit. If you book without an account, your name and email are pseudonymized behind a one-way reference immediately and kept separately from the booking record itself — see "How long we keep it" below for how quickly that's removed.</li>
          <li style={S.li}><strong>Tour plans:</strong> the criteria you enter (dates, budget, interests) and the resulting itinerary. Guest plans (made without an account) are automatically deleted after a short retention window; plans attached to a registered account are kept for the life of the account.</li>
          <li style={S.li}><strong>Conversational planning:</strong> if you use the chat-based planner, the free-text messages you send are processed to build your itinerary.</li>
        </ul>

        <h2 style={S.h2}>Why we process it</h2>
        <p style={S.p}>
          To provide the service you asked for: building tour plans, processing bookings, and letting winery
          managers run their listings. We do not sell personal data or use it for advertising.
        </p>

        <h2 style={S.h2}>Who we share it with</h2>
        <p style={S.p}>
          Booking details (your name, email, party size, date/time) are shared with the winery you book so they
          can confirm or decline your visit. We use Amazon Web Services (AWS) to host the application, store
          data, and send confirmation emails — all processing happens within AWS's Frankfurt (eu-central-1)
          region, in the EU.
        </p>

        <h2 style={S.h2}>How long we keep it</h2>
        <p style={S.p}>
          Guest tour plans (no account) expire automatically. Registered account data is kept for as long as
          your account is active; if you don't sign in for 3 years, we anonymize it automatically. Booking
          records made by a registered account keep their date/status for the winery's history, but we remove
          your name and email from the booking 24 months after the visit date. If you delete your account
          yourself, your personal details are anonymized immediately.
        </p>
        <p style={S.p}>
          If you book without an account, your name and email are held separately from the booking record
          (which itself never stores them) and are deleted automatically within 24 hours of your visit — far
          sooner than the 24-month policy above, since there's no account for that email to belong to. Every
          booking status update we send includes a link to delete it sooner if you'd rather not wait; that page
          explains what deleting it early means (the winery won't be able to reach you about that booking). If
          you later create an account with the same email, we link your past guest bookings to it instead of
          deleting them, so they show up in My Bookings.
        </p>

        <h2 style={S.h2}>Your rights</h2>
        <p style={S.p}>You can, at any time, from your Account page:</p>
        <ul style={{ margin: "0 0 12px", paddingLeft: 18 }}>
          <li style={S.li}>Download a copy of your data ("Export my data").</li>
          <li style={S.li}>Delete your account, which anonymizes your personal details.</li>
          <li style={S.li}>Update your saved preferences and profile information.</li>
        </ul>
        <p style={S.p}>
          You can also contact us at <a href={`mailto:${CONTACT_EMAIL}`} style={S.link}>{CONTACT_EMAIL}</a> to
          ask about, correct, or object to how your data is used, or to lodge a complaint with your local data
          protection authority.
        </p>

        <h2 style={S.h2}>Security</h2>
        <p style={S.p}>
          Data is encrypted at rest and in transit. Passwords are hashed and never stored or logged in plain text.
        </p>

        <h2 style={S.h2}>Changes</h2>
        <p style={S.p}>
          If we materially change this policy, we'll update the effective date above and ask existing users to
          re-confirm at next sign-in where required.
        </p>
      </div>
    </div>
  );
}
